import { IFlow } from '../Home';
import { viewSaleChannelFlowPDF } from '../ViewSalesChannelFlow';
import { useCallback, useMemo, useState } from 'react';
import { Button, Flex, Heading, Link, Spinner, SystemStyleObject, Text, useDisclosure } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ViewIndicatorIcon, ViewSellersIndicatorIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import { ROUTES } from 'config/routes';
import { format } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import AttentionPoints from 'app/components/AttentionPoints';
import FormsChannelFlow, { flowSchema } from 'app/components/FormsChannelFlow';
import IndicatorsModal from 'app/components/IndicatorsModal';
import Select, { IOption } from 'app/components/Select';
import SellersIndicatorsModal from 'app/components/SellersIndicatorsModal';
import ServiceChannelsTable from 'app/components/ServiceChannelsTable';
import Stepper from 'app/components/Stepper';
import TableGroup, { IColumnTableGroup, IRowTableGroup } from 'app/components/TableGroup';
import UserFilter from 'app/components/UserFilter';
import WageForm from 'app/components/WageForm';
import { useCalcFields } from 'hooks/useCalcFields';
import { useCommission } from 'hooks/useCommission';
import { useSession } from 'hooks/useSession';
import { useCustomToast } from 'hooks/useToast';
import { getAllCompanies, IResponseCompanies } from 'services/http/company';
import {
	createFlow,
	getAttendenceSimulatorValues,
	getFlowById,
	getIndicators,
	getSalaryCalculationValues,
	IMonthsOfTheYear,
	IRequestCreateFlow,
	IResponseAttendenceSimulatorValues,
	IResponseCreateFlow,
	IResponseGetFlowById,
	IResponseGetIndicators,
	IResponseSalaryCalculationValues,
} from 'services/http/flow';
import { getIndicatorsFilteredExpanded, IResponseIndicatorsExpanded } from 'services/http/history';
import { getAllNisaParameters, IResponseNisaParameters } from 'services/http/nisaParameters';
import { getListEmployeesByCompany } from 'services/http/user';
import { User, UserCompany } from 'types/user';
import { buildFlowPayload } from 'utils/buildFlowPayload';
import {
	DEPARTMENT_ACCESS_LEVELS_EMPLOYEE_FILTER,
	DEPARTMENT_OPTION_PARAMS,
	INDICATOR_TO_SEGMENT,
	NISA_PARAMETERS,
	PERIODS,
	SEGMENT_TABLE,
	SEGMENT_TYPE_CURRENCY,
	SEGMENT_TYPE_PERCENTAGE,
	SEGMENT_TYPE_STRING,
} from 'utils/constants';
import { findEmptyCurrentMonthParameters } from 'utils/findEmptyCurrentMonthParameters';
import { generateMonths } from 'utils/generateMonthsForTable';
import { handleDisableSelect } from 'utils/handlePermissions';
import { formatDecimalNumber, formatNumbers } from 'utils/Numbers';
import { parseErrors, parseErrorsFlow, ResponseErrors } from 'utils/parseErrors';

const RegisterSalesChannelFlow = () => {
	const styles: Record<string, SystemStyleObject> = {
		wrapperContainer: {
			padding: {
				base: '0 1.8rem',
				sm: '0 2.8rem',
				md: '0 2.5rem',
			},
			width: '100%',
			maxWidth: '143.313rem',
			margin: '0 auto',
			mt: '3.125rem',
			mb: '3.125rem',
			display: 'flex',
			justifyContent: 'center',
			flexDirection: 'column',
		},
		spinnerBox: {
			justifyContent: 'center',
		},
		container: {
			width: '100%',
			maxWidth: '143.313rem',
			mb: '3.125rem',
			justifyContent: 'center',
			flexDirection: 'column',
			gap: '2.5rem',
			mt: '2.5rem',
		},
		title: {
			fontWeight: '700',
			fontSize: '2.5rem',
		},
		wrapper: {
			w: '100%',
			gap: '1rem',
			height: 'max-content',
			position: 'relative',
			flexDirection: {
				base: 'column',
				'2xl': 'row',
			},
		},
		firstColumn: {
			flexDirection: 'column',
			gap: '2.5rem',
		},
		secundColumn: {
			w: {
				base: '100%',
				'2xl': '67%',
			},
			flexDirection: 'column',
			height: '100%',
			position: 'sticky',
			top: '0',
		},
		subTitle: {
			fontWeight: 600,
			fontSize: '1.875rem',
			textAlign: 'left',
			w: '100%',
		},
		firstTable: {
			flexDirection: 'column',
			gap: '2.5rem',
			mb: '2.688rem',
		},
		secundTable: {
			flexDirection: 'column',
		},
		filter: {
			flexDirection: 'column',
			mt: '2.5rem',
		},
		table: {
			maxH: '30rem',
		},
		successContainer: {
			flexDir: 'column',
			backgroundColor: 'white',
			borderColor: 'gray.300',
			borderWidth: '1px',
			borderRadius: '5px',
			padding: '2.5rem',
			alignItems: 'center',
		},
		successTitle: {
			fontWeight: '600',
			fontSize: '1.563rem',
			color: 'blue.800',
			mb: '1.5rem',
		},
		successButtons: {
			px: '1.688rem',
		},
		link: {
			display: 'flex',
			alignItems: 'center',
			color: 'black',
			fontSize: '1.5rem',
			gap: '0.5625rem',
		},
		button: {
			h: 'fit-content',
			color: '#2E769B',
			fontSize: '1.5rem',
			fontWeight: '600',
		},
		headerCustomStyle: { position: 'sticky', top: 0 },
		flexController: {
			flexDirection: { base: 'column', lg: 'row' },
			width: { base: '100%', lg: '52rem' },
			mb: '2rem',
			gap: '2.5rem',
		},
	};

	const navigate = useNavigate();
	const { addToast } = useCustomToast();

	const {
		session: { user },
	} = useSession();

	const isSelectDisabled = handleDisableSelect(user);

	const { onOpen: onOpenIndicators, isOpen: isOpenIndicators, onClose: onCloseIndicators } = useDisclosure();
	const { onOpen: onOpenSellers, isOpen: isOpenSellers, onClose: onCloseSellers } = useDisclosure();
	const {
		isOpen: isOpenAttentionPoints,
		onOpen: onOpenAttentionPoints,
		onClose: onCloseAttentionPoints,
	} = useDisclosure();

	const [currentStep, setCurrentStep] = useState(0);
	const [formValues, setFormValues] = useState<IFlow>({});

	let [searchParams] = useSearchParams();

	const company = searchParams.get('unidade') as string;
	const employeeId = searchParams.get('usuario') as string;
	const department = searchParams.get('departamento') as string;

	const departmentParam = DEPARTMENT_OPTION_PARAMS[department as keyof typeof DEPARTMENT_OPTION_PARAMS];

	const accessParam =
		DEPARTMENT_ACCESS_LEVELS_EMPLOYEE_FILTER[departmentParam as keyof typeof DEPARTMENT_ACCESS_LEVELS_EMPLOYEE_FILTER];

	const { mutate: getFlowMutation, isLoading: isLoadingFlow } = useMutation<
		IResponseGetFlowById,
		AxiosError<ResponseErrors>,
		void
	>(() => getFlowById(String(flow?.id)), {
		onSuccess: data => {
			viewSaleChannelFlowPDF(data);
		},
		onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
	});

	const { data: companies, isLoading: isCompaniesLoading } = useQuery<
		IResponseCompanies,
		AxiosError<ResponseErrors>,
		IResponseCompanies
	>(['companies'], () => getAllCompanies(), {
		onError: error => addToast({ type: 'error', title: 'Erro!', description: parseErrors(error?.response?.data) }),
		refetchOnMount: true,
		staleTime: 0,
		cacheTime: 0,
	});

	const { selectedCompany, selectedCompanyForModal } = useMemo(() => {
		const selectedCompany = companies?.companiesObjects?.find(item => String(item.id) === String(company));

		const selectedCompanyForModal: IOption = {
			label: selectedCompany?.company || '',
			value: selectedCompany?.id,
		};

		return { selectedCompany, selectedCompanyForModal };
	}, [companies?.companiesObjects, company]);

	const { data: employees, isLoading: isEmployeesLoading } = useQuery<
		UserCompany[],
		AxiosError<ResponseErrors>,
		UserCompany[]
	>(
		['employees', company, department, accessParam],
		() => getListEmployeesByCompany({ company, department: departmentParam, access_profile: accessParam }),
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
			staleTime: 0,
			cacheTime: 0,
		},
	);

	const { selectedEmployee, selectedEmployeeForModal } = useMemo(() => {
		const selectedEmployee = employees?.find(item => String(item.id) === employeeId) || ({} as UserCompany);

		const selectedEmployeeForModal: IOption = {
			label: selectedEmployee.username || '',
			value: selectedEmployee.id,
		};

		return { selectedEmployee, selectedEmployeeForModal };
	}, [employeeId, employees]);

	const { transformedEmployee, transformedEmployees } = useMemo(() => {
		const employee = {
			...selectedEmployee,
			company: selectedEmployee?.company?.map(cpn => String(cpn?.id)),
		};

		const employeesService: User[] =
			employees?.map(employee => ({
				...employee,
				company: employee.company?.map(cpn => String(cpn.id)),
			})) || [];

		return {
			transformedEmployee: employee,
			transformedEmployees: employeesService,
		};
	}, [employees, selectedEmployee]);

	const monthsAmount = 13;
	const months = generateMonths(monthsAmount);
	const monthsOptions: IOption[] = useMemo(() => {
		return months?.map(key => {
			return {
				value: key?.key,
				label: key?.label,
			};
		});
	}, [months]);

	const { control, watch } = useForm<{
		segment?: IOption | null;
		nisaPeriod?: IOption | null;
		period?: IOption | null;
	}>({
		defaultValues: {
			segment: null,
			nisaPeriod: monthsOptions[0],
			period: PERIODS[0],
		},
	});

	const schema = yup.object().shape(flowSchema);

	const registerForm = useForm<IFlow>({
		resolver: yupResolver(schema),
		defaultValues: {},
	});

	const segmentValue = watch('segment')?.value;
	const periodValue = watch('period')?.value;

	const { data: indicatorsExpandedFilteredData, isLoading: indicatorsExpandedFilteredDataIsLoading } = useQuery<
		IResponseIndicatorsExpanded,
		AxiosError<ResponseErrors>,
		IResponseIndicatorsExpanded
	>(
		['history-indicators-expanded-filtered', employeeId, periodValue, company],
		() =>
			getIndicatorsFilteredExpanded({
				user__id: Number(employeeId),
				months: Number(periodValue),
				user__company_id__id: Number(company),
			}),
		{
			onError: errors => addToast({ type: 'error', title: 'Erro!', description: parseErrors(errors?.response?.data) }),
			enabled: !!employeeId && !!periodValue && !!company,
		},
	);

	const { data: indicators } = useQuery<IResponseGetIndicators, AxiosError<ResponseErrors>, IResponseGetIndicators>(
		['indicators'],
		() => getIndicators(),
		{
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
		},
	);

	const segments: IOption[] = useMemo(() => {
		return Object.entries(indicators || []).map(([key, val]) => {
			return {
				value: key,
				label: val,
			};
		});
	}, [indicators]);

	const yearValue = watch('nisaPeriod')?.value;
	const [year, month] = String(yearValue)?.split('-');
	const monthKey = `month${month}`;

	const filteredSellerTrackingTable = (filter: string) => {
		const selectedValue = INDICATOR_TO_SEGMENT[filter];
		let values: Record<string, string> = {};

		const filteredValueIsString = SEGMENT_TYPE_STRING.find(item => item === selectedValue);
		const filteredValueIsPercentage = SEGMENT_TYPE_PERCENTAGE.find(item => item === selectedValue);
		const filteredValueIsCurrency = SEGMENT_TYPE_CURRENCY.find(item => item === selectedValue);

		if (filteredValueIsString) {
			values = {
				indicadores: SEGMENT_TABLE[filteredValueIsString],
				media: '',
				nisa: Math.round(Number(selectedMonthData?.formattedData?.[selectedValue] ?? '')).toString(),
				...generateTableData(filteredValueIsString),
			};
		}

		if (filteredValueIsPercentage) {
			values = {
				indicadores: SEGMENT_TABLE[filteredValueIsPercentage],
				media: '',
				nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.[selectedValue] ?? ''), '%'),
				...generateTableData(filteredValueIsPercentage, true),
			};
		}

		if (filteredValueIsCurrency) {
			values = {
				indicadores: SEGMENT_TABLE[filteredValueIsCurrency],
				media: '',
				nisa: formatNumbers(Number(selectedMonthData?.formattedData?.[selectedValue] ?? ''), true),
				...generateTableData(filteredValueIsCurrency, false, true),
			};
		}

		let data: IRowTableGroup[] = [
			{
				showHeaderGroup: false,
				children: [values],
			},
		];

		return data;
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateIndicator = (indicator: IMonthsOfTheYear) => {
		const updatedIndicator: IMonthsOfTheYear = { avarege: indicator?.avarege * 100 };
		months.forEach(({ key: monthkey }) => {
			updatedIndicator[monthkey] = indicator?.[monthkey] * 100;
		});
		return updatedIndicator;
	};

	const transformedData = useMemo(() => {
		const key = indicatorsExpandedFilteredData ? Object.keys(indicatorsExpandedFilteredData)[0] : undefined;
		const data = key ? indicatorsExpandedFilteredData?.[key] : undefined;

		if (Array.isArray(data)) {
			return data.reduce((acc, item) => {
				const key = Object.keys(item)[0];
				acc[key] = item[key];
				return acc;
			}, {});
		}

		return {};
	}, [indicatorsExpandedFilteredData]);

	const generateTableData = useCallback(
		(key: string, formatNumber = false, formatCurrency = false) => {
			let indicatorsExpandedDataFormated = { ...transformedData };

			if (transformedData) {
				indicatorsExpandedDataFormated = {
					...transformedData,
					txEvaluation: updateIndicator(transformedData.txEvaluation),
					txPurchase: updateIndicator(transformedData.txPurchase),
					txCapture: updateIndicator(transformedData.txCapture),
					conversion: updateIndicator(transformedData.conversion),
				};
			}

			const isInteger = !formatNumber && !formatCurrency;
			let decimalPlaces = isInteger ? 0 : 2;

			if (isInteger && indicatorsExpandedDataFormated[key]?.avarege) {
				indicatorsExpandedDataFormated[key].avarege = Math.round(indicatorsExpandedDataFormated[key].avarege);
			}

			let results = {};

			if (!indicatorsExpandedDataFormated || !Object.keys(indicatorsExpandedDataFormated).length) {
				return [];
			}

			results = {
				media: formatNumber
					? formatDecimalNumber(indicatorsExpandedDataFormated?.[key]?.['avarege'], '%')
					: formatCurrency
					? formatNumbers(indicatorsExpandedDataFormated?.[key]?.['avarege'] || 0, true)
					: indicatorsExpandedDataFormated?.[key]?.['avarege']
					? formatNumbers(indicatorsExpandedDataFormated?.[key]?.['avarege'] || 0, false, decimalPlaces)
					: '',
			};

			months?.forEach(({ key: monthkey }) => {
				results = {
					...results,
					[monthkey]: formatNumber
						? formatDecimalNumber(Number(indicatorsExpandedDataFormated?.[key]?.[monthkey]) || 0, '%')
						: formatCurrency
						? formatNumbers(indicatorsExpandedDataFormated?.[key]?.[monthkey] || 0, true)
						: String(indicatorsExpandedDataFormated?.[key]?.[monthkey] || ''),
				};
			});

			return results;
		},
		[months, transformedData, updateIndicator],
	);

	const { data: nisaParameters } = useQuery<
		IResponseNisaParameters[],
		AxiosError<ResponseErrors>,
		IResponseNisaParameters[]
	>(
		['nisaParameters', selectedCompanyForModal, departmentParam, yearValue, selectedEmployeeForModal],
		() =>
			getAllNisaParameters({
				company_id: String(selectedCompanyForModal.value || ''),
				department: String(departmentParam || ''),
				year: Number(year),
			}),
		{
			onSuccess: data => {
				const emptyParams = findEmptyCurrentMonthParameters(data, month);

				if (emptyParams.length > 0) {
					const parametersNames = emptyParams.map(param => NISA_PARAMETERS[param as keyof typeof NISA_PARAMETERS]);

					addToast({
						type: 'error',
						title: 'Parâmetros do mês/ano selecionado não foram cadastrados!',
						description: `Os seguintes parâmetros estão vazios: ${[parametersNames].join(',  ')}`,
					});
				}
			},
			onError: errors =>
				addToast({
					type: 'error',
					title: 'Erro!',
					description: parseErrors(errors?.response?.data),
				}),
			enabled: !!selectedCompanyForModal.value && !!departmentParam && !!yearValue && !!selectedEmployeeForModal.value,
		},
	);

	const selectedMonthData = useMemo(() => {
		if (!nisaParameters || !month || !nisaParameters.length) return { formattedData: {}, hasInvalidValues: false };

		const item = nisaParameters[0];
		const formattedData: Record<string, string | number | null> = {};
		let hasInvalidValues = false;

		Object.keys(item).forEach(key => {
			if (
				typeof item[key as keyof IResponseNisaParameters] === 'object' &&
				item[key as keyof IResponseNisaParameters] !== null
			) {
				const value = (item[key as keyof IResponseNisaParameters] as IMonthsOfTheYear)[`${monthKey}`];

				if (value !== null && value !== undefined && String(value).trim() !== '0.00' && Number(value) !== 0) {
					formattedData[key] = value;
				} else {
					hasInvalidValues = true;
				}
			}
		});

		return { formattedData, hasInvalidValues };
	}, [nisaParameters, month, monthKey]);

	const previousMonths = months.slice(1);

	const filteredMonths = useMemo(() => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth() + 1;
		const currentMonthKey = `${currentYear}-${currentMonth}`;

		const monthsWithoutCurrent = previousMonths.filter(month => month.key !== currentMonthKey);

		return monthsWithoutCurrent.slice(0, Number(periodValue) || 12);
	}, [previousMonths, periodValue]);

	const indicatorsTable = useMemo(() => {
		return {
			columns: [
				{
					label: 'INDICADORES',
					key: 'indicadores',
				},
				{
					label: 'PARÂMETRO NISA',
					key: 'nisa',
					columnCustomStyle: {
						textAlign: 'center',
					},
				},
				{
					label: 'MÉDIA',
					key: 'media',
					columnCustomStyle: {
						textAlign: 'center',
					},
				},

				...filteredMonths,
			] as IColumnTableGroup[],
			data: [
				{
					showHeaderGroup: false,
					children: [
						{
							indicadores: 'Fluxo de Loja (Passantes)',
							// nisa: Math.round(Number(selectedMonthData?.formattedData?.fluxoLojaPassante)),
							...generateTableData('fluxoLojaPassante'),
						},
						{
							indicadores: 'Fluxo de Ligaçoes Recebidas',
							// nisa: Math.round(Number(selectedMonthData?.formattedData?.fluxoLigacoes)),
							...generateTableData('fluxoLigacoes'),
						},
						{
							indicadores: 'Comparecimento Ligações',
							nisa: Math.round(Number(selectedMonthData?.formattedData?.comparecimentoLigacoes)),
							...generateTableData('comparecimentoLigacoes'),
						},
						{
							indicadores: 'NPS',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.nps), '%'),
							...generateTableData('nps', true),
						},
						{
							indicadores: 'Leads',
							...generateTableData('leads'),
						},
						{
							indicadores: 'Oficina',
							nisa: Math.round(Number(selectedMonthData?.formattedData?.oficina)),
							...generateTableData('oficina'),
						},
						{
							indicadores: 'Fluxo Loja Vendedor',
							// nisa: Math.round(Number(selectedMonthData?.formattedData?.fluxoLojaVendedor)),
							...generateTableData('fluxoLojaVendedor'),
						},
						{
							indicadores: 'Ligações Recebidas',
							// nisa: Math.round(Number(selectedMonthData?.formattedData?.ligacoesRecebidas)),
							...generateTableData('ligacoesRecebidas'),
						},
						{
							indicadores: 'Comparecimento',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.comparecimento), '%'),
							...generateTableData('comparecimento'),
						},
						{
							indicadores: 'Conversão',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.conversao), '%'),
							...generateTableData('conversion', true),
						},
					],
					columnCustomStyle: {
						backgroundColor: 'red',
					},
				},
				{
					showHeaderGroup: true,
					indicadores: 'VENDAS TOTAL',
					// nisa: Math.round(Number(selectedMonthData?.formattedData?.totalSales)),
					...generateTableData('totalSales'),
					children: [
						{
							indicadores: 'Faturamento',
							nisa: selectedMonthData?.formattedData?.billings
								? formatNumbers(Number(selectedMonthData?.formattedData?.billings), true)
								: '',
							...generateTableData('billings', false, true),
						},
						{
							indicadores: 'Ticket Médio',

							nisa: selectedMonthData?.formattedData?.averageTicket
								? formatNumbers(Number(selectedMonthData?.formattedData?.averageTicket), true)
								: '',
							...generateTableData('averageTicket', false, true),
						},
						{
							indicadores: 'Despesas com Cortesias',
							nisa: selectedMonthData?.formattedData?.complimentaryExpenses
								? formatNumbers(Number(selectedMonthData?.formattedData?.complimentaryExpenses), true)
								: '',

							...generateTableData('complimentaryExpenses', false, true),
						},
						{
							indicadores: '% M.B',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.percentageMb), '%'),
							...generateTableData('percentageMb', true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'FINANCIAMENTO',
					nisa: selectedMonthData?.formattedData?.financing
						? formatNumbers(Number(selectedMonthData?.formattedData?.financing), true)
						: '',

					...generateTableData('financing', false, true),

					children: [
						{
							indicadores: 'Contratos Financiamento',
							nisa: Math.round(Number(selectedMonthData?.formattedData?.financingContracts)),
							...generateTableData('financingContracts'),
						},
						{
							indicadores: 'Penetracion Financiamento',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.financingPenetration), '%'),
							...generateTableData('financingPenetration', true),
						},
						{
							indicadores: 'Financiamento fora',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.financingEvasion), '%'),
							...generateTableData('financingEvasion', true),
						},
						{
							indicadores: 'Penetration Valor Financiado',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.financingValuePenetration), '%'),
							...generateTableData('financingValuePenetration', true),
						},
						{
							indicadores: 'Retorno por Carro',

							nisa: selectedMonthData?.formattedData?.carReturn
								? formatNumbers(Number(selectedMonthData?.formattedData?.carReturn), true)
								: '',
							...generateTableData('carReturn', false, true),
						},
						{
							indicadores: 'Rentabilidade Financiamento',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.financingProfitability), '%'),
							...generateTableData('financingProfitability', true),
						},
						{
							indicadores: 'Retorno total',

							...generateTableData('totalReturn', false, true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'TCCB',
					nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.tccb), '%'),
					...generateTableData('tccb', true),
					children: [
						{
							indicadores: 'TCCB por Contrato',
							nisa: selectedMonthData?.formattedData?.tccbByContract
								? formatNumbers(Number(selectedMonthData?.formattedData?.tccbByContract), true)
								: '',

							...generateTableData('tccbByContract', false, true),
						},
						{
							indicadores: 'TCCB por Carro',
							// nisa: selectedMonthData?.formattedData?.tccbByCar
							// 	? formatNumbers(Number(selectedMonthData?.formattedData?.tccbByCar), true)
							// 	: '',

							...generateTableData('tccbByCar', false, true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'Emplacamento',
					nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.placement), '%'),
					...generateTableData('placement', true),

					children: [
						{
							indicadores: 'Emplacamento por carro',
							nisa: selectedMonthData?.formattedData?.placementByCar
								? formatNumbers(Number(selectedMonthData?.formattedData?.placementByCar), true)
								: '',

							...generateTableData('placementByCar', false, true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'Captação',
					nisa: Math.round(Number(selectedMonthData?.formattedData?.capture)),
					...generateTableData('capture'),

					children: [
						{
							indicadores: 'Avaliação',
							nisa: Math.round(Number(selectedMonthData?.formattedData?.evaluation)),
							...generateTableData('evaluation'),
						},
						{
							indicadores: 'Taxa avaliação',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.txEvaluation), '%'),
							...generateTableData('txEvaluation', true),
						},
						{
							indicadores: 'Taxa compra',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.txBuy), '%'),
							...generateTableData('txPurchase', true),
						},
						{
							indicadores: 'Taxa captura',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.txCapture), '%'),
							...generateTableData('txCapture', true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'Margem de contribuição',
					nisa: selectedMonthData?.formattedData?.contributionMargin
						? formatNumbers(Number(selectedMonthData?.formattedData?.contributionMargin), true)
						: '',

					...generateTableData('contributionMargin', false, true),

					children: [
						{
							indicadores: 'Lucro Bruto Total',
							nisa: selectedMonthData?.formattedData?.totalGrossProfit
								? formatNumbers(Number(selectedMonthData?.formattedData?.totalGrossProfit), true)
								: '',

							...generateTableData('contributionMargin2', false, true),
						},
						{
							indicadores: '(%) Lucro Bruto s/Faturamento',
							nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.grossProfitwithoutRevenue), '%'),
							...generateTableData('contributionMargin3', true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'Acessórios',
					nisa: selectedMonthData?.formattedData?.accessories
						? formatNumbers(Number(selectedMonthData?.formattedData?.accessories), true)
						: '',
					...generateTableData('accessoriesSale', false, true),

					children: [
						{
							indicadores: 'Ticket Médio Acessorios',
							nisa: selectedMonthData?.formattedData?.mediumAccessoryTicket
								? formatNumbers(Number(selectedMonthData?.formattedData?.mediumAccessoryTicket), true)
								: '',

							...generateTableData('averageTicketAccessories', false, true),
						},
					],
				},
				{
					showHeaderGroup: true,
					indicadores: 'HGSI',
					nisa: formatDecimalNumber(Number(selectedMonthData?.formattedData?.hgsi), '%'),
					...generateTableData('hgsi', true),
				},
			] as IRowTableGroup[],
		};
	}, [generateTableData, filteredMonths, selectedMonthData]);

	const indicatorsTableData = useMemo(
		() => {
			const segmentValue = watch('segment')?.value;

			return segmentValue ? filteredSellerTrackingTable(String(segmentValue)) : indicatorsTable?.data;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[watch('segment'), indicatorsTable?.data],
	);

	const { data: attendenceSimulator } = useQuery<
		IResponseAttendenceSimulatorValues,
		AxiosError<ResponseErrors>,
		IResponseAttendenceSimulatorValues
	>([employeeId, 'atendenceSimulator'], () => getAttendenceSimulatorValues(Number(employeeId)), {
		onError: errors =>
			addToast({
				type: 'error',
				title: 'Erro!',
				description: parseErrors(errors?.response?.data),
			}),
		enabled: !!employeeId,
	});

	const { data: salaryCalculationValues } = useQuery<
		IResponseSalaryCalculationValues,
		AxiosError<ResponseErrors>,
		IResponseSalaryCalculationValues
	>(['salaryCalculationValues'], () => getSalaryCalculationValues(), {
		onError: errors =>
			addToast({
				type: 'error',
				title: 'Erro!',
				description: parseErrors(errors?.response?.data),
			}),
	});

	const { handleCommission, isCommissionsLoading } = useCommission({
		company: selectedCompany?.id,
		department: departmentParam,
	});

	const {
		mutate: createFlowMutate,
		isSuccess: isCreateFlowSuccess,
		isLoading: isLoadingCreateFlow,
		data: flow,
	} = useMutation<IResponseCreateFlow, AxiosError<ResponseErrors>, IRequestCreateFlow>(data => createFlow(data), {
		onError: errors => {
			addToast({
				type: 'error',
				title: 'Erro!',
				description: parseErrorsFlow(errors?.response?.data),
			});
		},
	});

	const onSubmitForm = (data: IFlow) => {
		setFormValues(prevState => ({ ...prevState, ...data }));
	};

	const onCreateFlowMutate = (data: IFlow) => {
		if (!employeeId)
			return addToast({
				type: 'warning',
				title: 'Alerta',
				description: 'Selecione um vendedor para continuar!',
			});

		const selectedCompany = companies?.companiesObjects?.find(cpn => String(cpn?.id) === company);

		onSubmitForm(data);

		const payload = buildFlowPayload(data, {
			company: {
				company: selectedCompany?.company || '',
				autoAvaliarCompanyName: selectedCompany?.autoAvaliarCompanyName || '',
				nisaBiCompanyName: selectedCompany?.nisaBiCompanyName || '',
				nisaSyonetCompanyId: selectedCompany?.nisaSyonetCompanyId || 0,
			},
			user: Number(employeeId),
			department: departmentParam,
			monthYear: String(format(new Date(), 'yyyy-MM-dd')),
		});

		createFlowMutate(payload);
	};

	const averageQuatitySeller = Math.round(
		Number(attendenceSimulator?.averageStoreFlowPassing || 0) /
			Math.max(Number(attendenceSimulator?.totalSellersInCompany || 1), 1),
	);

	const { formCalc } = useCalcFields({
		formValues,
		handleCommission,
		salaryCalculationValues,
		placementMonthlyData: transformedData?.placementAmount,
		placementYearValue: String(yearValue),
		department: departmentParam,
	});

	const onOpenAttentionPointsModal = () => {
		onOpenAttentionPoints();
	};

	const elements = [
		<FormsChannelFlow
			key="FormsChannelFlow"
			formChannelFlow={registerForm}
			attendenceSimulator={attendenceSimulator}
			averageQuatitySeller={averageQuatitySeller}
			onSubmit={onSubmitForm}
			defaultValues={formCalc}
			onOpenAttentionPointsModal={onOpenAttentionPointsModal}
			isLoadingButton={isCommissionsLoading}
		/>,
		<WageForm
			key="WageForm"
			formChannelFlow={registerForm}
			onSubmit={onCreateFlowMutate}
			watching={onSubmitForm}
			defaultValues={formCalc}
			salaryCalculationValues={salaryCalculationValues}
			isLoadingButton={isLoadingCreateFlow}
			onOpenAttentionPointsModal={onOpenAttentionPointsModal}
		/>,
	];

	return (
		<Flex sx={styles.wrapperContainer}>
			<Flex>
				<Link sx={styles.link} onClick={() => navigate('-1')}>
					<ArrowLeftIcon />
					Voltar
				</Link>
			</Flex>
			<Flex sx={styles.container}>
				<Heading sx={styles.title}>Cadastro de Objetivos do Mês</Heading>
				{isCompaniesLoading || isEmployeesLoading ? (
					<Flex sx={styles.spinnerBox}>
						<Spinner />
					</Flex>
				) : (
					<>
						<UserFilter
							company={company}
							department={department}
							employee={transformedEmployee}
							companies={companies?.companiesObjects || []}
							employees={transformedEmployees || []}
							labelEmployee="Vendedor"
							title="Seleção de vendedor"
							isReadOnly={currentStep > 0}
							showDepartment
							isUserPermited={isSelectDisabled}
						/>
						{isCreateFlowSuccess ? (
							<Flex sx={styles.container}>
								<Text sx={styles.subTitle}>Cadastro de novo fluxo</Text>
								<Flex sx={styles.successContainer}>
									<Text sx={styles.successTitle} data-testid="success-message">
										Cadastro realizado com sucesso!
									</Text>
									<Text mb="4.375rem">
										Visualize o documento de Geração de fluxo por Canais de vendas ou confira o histórico:
									</Text>
									<Flex gap="1.25rem">
										<Button isLoading={isLoadingFlow} sx={styles.successButtons} onClick={() => getFlowMutation()}>
											Visualizar documento
										</Button>
										<Button
											sx={styles.successButtons}
											variant="outlined"
											onClick={() => navigate(ROUTES.listSalesChannelsFlow)}
										>
											Ver histórico
										</Button>
									</Flex>
								</Flex>
							</Flex>
						) : (
							<Flex sx={styles.wrapper}>
								<Flex sx={styles.firstColumn}>
									<Text sx={styles.subTitle}>Cadastro de novo fluxo</Text>
									<Stepper elements={elements} currentStep={setCurrentStep} />
								</Flex>
								<Flex sx={styles.secundColumn}>
									{elements.length - 1 === currentStep && (
										<Flex sx={styles.firstTable}>
											<Text sx={styles.subTitle}>Canais de atendimento</Text>
											<ServiceChannelsTable
												defaultValues={formCalc}
												watching={onSubmitForm}
												onSubmit={onSubmitForm}
												control={registerForm.control}
												getValues={registerForm.getValues}
												reset={registerForm.reset}
												watch={registerForm.watch}
												errors={registerForm.formState.errors}
											/>
										</Flex>
									)}
									{currentStep < elements.length - 1 && !indicatorsExpandedFilteredDataIsLoading && (
										<Flex sx={styles.secundTable}>
											<Text sx={styles.subTitle}>
												{periodValue && periodValue > '1'
													? `Consulta dos ${periodValue} meses anteriores`
													: 'Consulta do mês corrente'}
											</Text>
											<Flex sx={styles.filter}>
												<Flex sx={styles?.flexController}>
													<Controller
														name="segment"
														control={control}
														render={({ field: { value, onChange } }) => (
															<Select
																label="Indicador"
																name="segment"
																placeholder="Indicador"
																dataTestId="input--segment"
																variant="medium"
																options={segments}
																onChange={onChange}
																value={value}
																isClearable
															/>
														)}
													/>
													<Controller
														name="nisaPeriod"
														control={control}
														render={({ field: { value, onChange } }) => (
															<Select
																label="Período NISA"
																name="nisaPeriod"
																placeholder="Período NISA"
																dataTestId="input--nisa-period"
																variant="medium"
																options={monthsOptions}
																onChange={e => {
																	onChange(e);
																}}
																value={value}
															/>
														)}
													/>
													<Controller
														name="period"
														control={control}
														render={({ field: { value, onChange } }) => (
															<Select
																label="Período Média"
																name="period"
																placeholder="Selecione o período"
																dataTestId="input--period"
																variant="medium"
																options={PERIODS}
																onChange={e => {
																	onChange(e);
																}}
																value={value}
															/>
														)}
													/>
												</Flex>
												<TableGroup
													striped
													data={indicatorsTableData}
													columns={indicatorsTable.columns}
													contentCustomStyle={{
														...styles.table,
														tbody: {
															tr: {
																'&:nth-of-type(-n + 4)': {
																	td: {
																		background: segmentValue ? 'initial' : '#C1D6E1',
																	},
																},
															},
														},
													}}
													variant="small"
													headerCustomStyle={styles?.headerCustomStyle}
												/>
											</Flex>

											<Flex gap="5rem" mt="1.75rem">
												<Button
													variant="unstyled"
													leftIcon={<ViewIndicatorIcon />}
													sx={styles.button}
													onClick={onOpenIndicators}
												>
													Ver todos os indicadores
												</Button>
												<Button
													variant="unstyled"
													leftIcon={<ViewSellersIndicatorIcon />}
													sx={styles.button}
													onClick={onOpenSellers}
												>
													Ver consulta de funcionários
												</Button>
											</Flex>
										</Flex>
									)}
								</Flex>
							</Flex>
						)}
					</>
				)}
			</Flex>
			<SellersIndicatorsModal
				isOpen={isOpenSellers}
				onClose={onCloseSellers}
				company={selectedCompanyForModal}
				department={department}
			/>
			<IndicatorsModal
				isOpen={isOpenIndicators}
				onClose={onCloseIndicators}
				company={selectedCompanyForModal}
				employee={selectedEmployeeForModal}
				department={department}
			/>
			<AttentionPoints
				isOpen={isOpenAttentionPoints}
				onClose={onCloseAttentionPoints}
				control={registerForm.control}
				errors={registerForm.formState.errors}
			/>
		</Flex>
	);
};

export default RegisterSalesChannelFlow;
